// Sdílené parsování BCF archivu (2.1 i 3.0) do normalizovaného modelu (§3, §4).
// Tolerantní ke strukturálním rozdílům verzí (Comments/Viewpoints jako potomci
// Markup i Topic). Zachovává původní GUID, autory, data, stavy, reference (§4).
import { parseXML, child, children, text, attr, vec3, num } from "./xml.js";
import { createTopic, createComment, createViewpoint, componentRef, documentReference } from "../core/model.js";

const td = new TextDecoder();
function decode(bytes) { return typeof bytes === "string" ? bytes : td.decode(bytes); }
function firstByTag(root, tag) { const l = root.getElementsByTagName(tag); return l && l.length ? l[0] : null; }
function allByTag(root, tag) { return Array.from(root.getElementsByTagName(tag)); }

function parseCamera(vis) {
  const persp = firstByTag(vis, "PerspectiveCamera");
  const ortho = firstByTag(vis, "OrthogonalCamera");
  const cam = persp || ortho;
  if (!cam) return null;
  const out = {
    type: persp ? "perspective" : "orthogonal",
    position: vec3(child(cam, "CameraViewPoint")),
    direction: vec3(child(cam, "CameraDirection")),
    up: vec3(child(cam, "CameraUpVector")),
  };
  if (persp) out.fieldOfView = num(text(cam, "FieldOfView")) || 60;
  else out.viewToWorldScale = num(text(cam, "ViewToWorldScale"));
  return out;
}

function parseComponentRef(compEl) {
  return componentRef({
    globalId: attr(compEl, "IfcGuid") || text(compEl, "IfcGuid"),
    authoringToolId: text(compEl, "AuthoringToolId"),
    originatingSystem: text(compEl, "OriginatingSystem"),
  });
}

function parseViewpointXML(bytes) {
  const doc = parseXML(decode(bytes));
  const vis = doc.documentElement;                 // VisualizationInfo
  const components = firstByTag(vis, "Components");
  const selection = [];
  const selEl = components && firstByTag(components, "Selection");
  if (selEl) for (const c of allByTag(selEl, "Component")) if (attr(c, "IfcGuid")) selection.push(parseComponentRef(c));

  const visEl = components && firstByTag(components, "Visibility");
  const visibility = { defaultVisibility: true, exceptions: [] };
  if (visEl) {
    const dv = attr(visEl, "DefaultVisibility");
    visibility.defaultVisibility = dv == null ? true : dv === "true";
    const exc = firstByTag(visEl, "Exceptions");
    if (exc) for (const c of allByTag(exc, "Component")) if (attr(c, "IfcGuid")) visibility.exceptions.push(parseComponentRef(c));
  }

  const clippingPlanes = [];
  const cps = firstByTag(vis, "ClippingPlanes");
  if (cps) for (const cp of children(cps, "ClippingPlane")) {
    const location = vec3(child(cp, "Location"));
    const direction = vec3(child(cp, "Direction"));
    if (location && direction) clippingPlanes.push({ location, direction });
  }

  return { camera: parseCamera(vis), selection, visibility, clippingPlanes };
}

// Parsuje jeden topic (markup.bcf) + jeho viewpointy/snímky z mapy souborů.
function parseTopicFolder(files, folder, version) {
  const markupBytes = files.get(folder + "/markup.bcf") || files.get(folder + "/markup.xml");
  if (!markupBytes) return null;
  const doc = parseXML(decode(markupBytes));
  const markup = doc.documentElement;              // Markup
  const topicEl = firstByTag(markup, "Topic");
  if (!topicEl) return null;

  const topic = createTopic({
    id: attr(topicEl, "Guid") || folder,
    title: text(topicEl, "Title"),
    description: text(topicEl, "Description"),
    type: attr(topicEl, "TopicType"),
    status: attr(topicEl, "TopicStatus") || "new",
    priority: text(topicEl, "Priority"),
    stage: text(topicEl, "Stage"),
    assignedTo: text(topicEl, "AssignedTo"),
    createdBy: text(topicEl, "CreationAuthor"),
    modifiedBy: text(topicEl, "ModifiedAuthor"),
    createdAt: text(topicEl, "CreationDate"),
    modifiedAt: text(topicEl, "ModifiedDate"),
    dueDate: text(topicEl, "DueDate"),
    sourceVersion: version,
  });
  topic.workflow = { state: topic.status };

  // Labels — 2.1: víc <Labels>; 3.0: <Labels><Label>.
  const labelsWrap = child(topicEl, "Labels");
  if (labelsWrap && labelsWrap.children.length) topic.labels = children(labelsWrap, "Label").map((l) => l.textContent.trim()).filter(Boolean);
  else topic.labels = children(topicEl, "Labels").map((l) => l.textContent.trim()).filter(Boolean);

  // DocumentReferences.
  const docRefs = firstByTag(topicEl, "DocumentReferences") || firstByTag(markup, "DocumentReferences");
  if (docRefs) for (const d of children(docRefs, "DocumentReference")) topic.documentReferences.push(documentReference({
    id: attr(d, "Guid"), referencedDocument: text(d, "ReferencedDocument") || attr(d, "ReferencedDocument"), description: text(d, "Description"), isExternal: attr(d, "isExternal") !== "false",
  }));

  // Viewpoints (Markup/Topic > Viewpoints > (ViewPoint|Viewpoints)).
  const vpEls = allByTag(markup, "ViewPoint").concat(allByTag(markup, "Viewpoints").filter((e) => attr(e, "Guid")));
  const seenVp = new Set();
  for (const vpEl of vpEls) {
    const guid = attr(vpEl, "Guid");
    if (!guid || seenVp.has(guid)) continue;
    seenVp.add(guid);
    const bcfvName = text(vpEl, "Viewpoint");
    const snapName = text(vpEl, "Snapshot");
    let parsed = { camera: null, selection: [], visibility: { defaultVisibility: true, exceptions: [] }, clippingPlanes: [] };
    if (bcfvName && files.get(folder + "/" + bcfvName)) { try { parsed = parseViewpointXML(files.get(folder + "/" + bcfvName)); } catch (_) {} }
    let snapshot = null;
    if (snapName && files.get(folder + "/" + snapName)) {
      const b = files.get(folder + "/" + snapName);
      snapshot = { type: "png", fileName: snapName, data: bytesToDataURL(b, "image/png") };
    }
    topic.viewpoints.push(createViewpoint({ id: guid, index: num(text(vpEl, "Index")), ...parsed, snapshot }));
  }

  // Comments.
  for (const cEl of allByTag(markup, "Comment")) {
    if (!attr(cEl, "Guid")) continue;
    const vpRef = child(cEl, "Viewpoint");
    topic.comments.push(createComment({
      id: attr(cEl, "Guid"),
      author: text(cEl, "Author"),
      date: text(cEl, "Date"),
      modifiedAuthor: text(cEl, "ModifiedAuthor"),
      modifiedDate: text(cEl, "ModifiedDate"),
      text: text(cEl, "Comment"),
      viewpointId: vpRef ? attr(vpRef, "Guid") : null,
    }));
  }
  return topic;
}

function bytesToDataURL(bytes, mime) {
  if (typeof bytes === "string") return bytes.startsWith("data:") ? bytes : null;
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return `data:${mime};base64,` + btoa(bin);
}

// Rozdělí soubory podle topic-složek (GUID adresář obsahující markup.bcf).
export function parseBCFArchive(files, version) {
  const folders = new Set();
  for (const path of files.keys()) {
    const m = path.match(/^([^/]+)\/markup\.(bcf|xml)$/);
    if (m) folders.add(m[1]);
  }
  const topics = [];
  for (const folder of folders) {
    try { const t = parseTopicFolder(files, folder, version); if (t) topics.push(t); }
    catch (_) { /* poškozený topic přeskočit, nezhroutit celý import (§29) */ }
  }
  return topics;
}
